"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const tslib_1 = require("tslib");
const HandlerManager_1 = tslib_1.__importDefault(require("./HandlerManager"));
const ValueChangeEvent_1 = tslib_1.__importDefault(require("./ValueChangeEvent"));
const LoadingProgressStatus_1 = require("../enums/LoadingProgressStatus");
class AsyncValueHolder {
    static extractAsyncValueHolderArgs(args) {
        if (!args[0].loader) {
            const [loader, onError, cacheValidator] = args;
            return { loader, onError, cacheValidator };
        }
        return args[0];
    }
    constructor(...args) {
        this.handlerManager = new HandlerManager_1.default();
        this.errorHandlerManager = new HandlerManager_1.default();
        this.statusHandlerManager = new HandlerManager_1.default();
        const { loader, onError, cacheValidator } = AsyncValueHolder.extractAsyncValueHolderArgs(args);
        this.loader = loader;
        this.onError = onError;
        this.cacheValidator = cacheValidator;
        this.dependencies = [];
        this.dependants = [];
    }
    setStale(skipDeps = false) {
        if (!skipDeps) {
            const dependencies = this.getOrderedDependencyList();
            for (const asyncValueHolder of dependencies) {
                asyncValueHolder.setStale(true);
            }
            return;
        }
        this.cacheValidator?.setStale();
        this.hasError = false;
        this.error = null;
        this.loading = false;
        this.setValue(undefined, true);
        this.isStale = true;
        this.statusChanged();
        // If we have any handlers interested in the value, then we update the cache
        if (this.handlerManager.hasHandlers() || this.statusHandlerManager.hasHandlers()) {
            this.ensureValue();
        }
    }
    clear(skipDeps = false) {
        if (!skipDeps) {
            const dependencies = this.getOrderedDependencyList();
            for (const asyncValueHolder of dependencies) {
                asyncValueHolder.clear(true);
            }
            return;
        }
        this.hasError = false;
        this.error = null;
        this.loading = false;
        // The value null signals that a (none) value has been established - reloads won't be attempted
        this.setValue(null, true);
    }
    getValue() {
        return this.value;
    }
    async getValueEnsured() {
        if (this.loading) {
            let handler;
            await new Promise((resolve, reject) => {
                handler = this.addValueChangeHandler(() => {
                    resolve();
                });
            });
            // @ts-ignore
            handler?.removeHandler();
            return this.value;
        }
        await this.ensureValue();
        return this.value;
    }
    /**
     * Adds handler to AsyncValueHolder.
     * Has the side effect of immediately getting the data, if value is undefined.
     * @param {ValueChangeEvent} handler The handler which will get an event when there is a change
     * @param notifyImmediatelyIfValue notify the handler immediately if there is a value
     */
    addValueChangeHandler(handler, notifyImmediatelyIfValue = false) {
        const registration = this.handlerManager.addHandler(handler);
        this.ensureValue(notifyImmediatelyIfValue ? handler : undefined);
        return registration;
    }
    addErrorHandler(handler) {
        return this.errorHandlerManager.addHandler(handler);
    }
    removeValueChangeHandler(handler) {
        this.handlerManager.removeHandler(handler);
    }
    removeErrorHandler(handler) {
        this.errorHandlerManager.removeHandler(handler);
    }
    setValue(value, fireEvents) {
        const oldValue = this.value;
        const oldError = this.hasError;
        this.value = value;
        this.loading = false;
        this.hasError = false;
        this.error = null;
        this.isStale = false;
        if (fireEvents && (oldError || value !== oldValue)) {
            this.handlerManager.fireEvent(new ValueChangeEvent_1.default(value));
            this.errorHandlerManager.fireEvent(new ValueChangeEvent_1.default(this.error));
        }
    }
    setError(fireEvents) {
        const oldError = this.hasError;
        this.value = null;
        this.loading = false;
        this.hasError = true;
        if (fireEvents) {
            if (this.hasError !== oldError) {
                // Fire at transition from success to error (and vice versa, but that is handled by setValue)
                this.handlerManager.fireEvent(new ValueChangeEvent_1.default(this.value));
                this.errorHandlerManager.fireEvent(new ValueChangeEvent_1.default(this.error));
                this.statusChanged();
            }
        }
    }
    isFailed() {
        return this.hasError;
    }
    isLoading() {
        return this.loading;
    }
    getStatus() {
        if (this.loading) {
            return LoadingProgressStatus_1.LoadingProgressStatus.LOADING;
        }
        if (this.hasError) {
            return LoadingProgressStatus_1.LoadingProgressStatus.ERROR;
        }
        if (this.isStale) {
            return LoadingProgressStatus_1.LoadingProgressStatus.IDLE;
        }
        return LoadingProgressStatus_1.LoadingProgressStatus.SUCCESS;
    }
    /**
     * Has the side effect of immediately getting the data, if value is undefined.
     * @param {ValueChangeEvent} handler The handler which will get an event when there is a status change
     */
    addStatusChangeHandler(handler) {
        const handlerRegistration = this.statusHandlerManager.addHandler(handler);
        this.ensureValue();
        return handlerRegistration;
    }
    /**
     * Order of updating:
     * 1. (Dependencies) Those this AsyncValueHolder depends on
     * 2. (This) This AsyncValueHolder itself
     * 3. (Dependants) Those AsyncValueHolders that depends on this AsyncValueHolder
     *  @param {AsyncValueHolder<any>[]} asyncValueHolders Dependencies: the AsyncValueHolders that this AsyncValueHolder depends on
     */
    addDependencies(...asyncValueHolders) {
        this.addDeps(asyncValueHolders, this.dependencies);
    }
    /**
     * Order of updating:
     * 1. (Dependencies) Those this AsyncValueHolder depends on
     * 2. (This) This AsyncValueHolder itself
     * 3. (Dependants) Those AsyncValueHolders that depends on this AsyncValueHolder
     *  @param {AsyncValueHolder<any>[]} asyncValueHolders Dependants: the AsyncValueHolders that depends on AsyncValueHolder
     */
    addDependants(...asyncValueHolders) {
        this.addDeps(asyncValueHolders, this.dependants);
    }
    addDeps(newDeps, deps) {
        const uniqueNewDeps = newDeps.filter(asyncValueHolder => !deps.includes(asyncValueHolder));
        deps.push(...uniqueNewDeps);
    }
    /**
     * Order of updating:
     * 1. (Dependencies) Those this AsyncValueHolder depends on
     * 2. (This) This AsyncValueHolder itself
     * 3. (Dependants) Those AsyncValueHolders that depends on this AsyncValueHolder
     *  @returns {AsyncValueHolder<any>[]} returns the order of how the AsyncValueHolder should update
     */
    getOrderedDependencyList() {
        return [...this.dependencies, this, ...this.dependants];
    }
    statusChanged() {
        this.statusHandlerManager.fireEvent(new ValueChangeEvent_1.default(this.getStatus()));
    }
    async ensureValue(handler) {
        if (this.hasError || this.loading) {
            return;
        }
        if (this.value === undefined) {
            // Cache is empty, just go ahead and load (no need for a cache validation in this case)
            await this.load();
            return;
        }
        if (this.cacheValidator) {
            const isValid = await this.cacheValidator.isValid();
            if (!isValid) {
                await this.load();
                return;
            }
            if (handler && isValid) {
                handler(new ValueChangeEvent_1.default(this.value));
                return;
            }
        }
        if (handler) {
            handler(new ValueChangeEvent_1.default(this.value));
            return;
        }
    }
    async load() {
        const loadInitiated = new Date();
        this.loading = true;
        this.statusChanged();
        this.value = undefined;
        try {
            const result = await this.loader();
            this.cacheValidator?.valueLoaded(result, loadInitiated);
            if (!result) {
                // We use undefined to mark the uninitialized value.
                // - Don't let an empty/undefined server response fool us.
                this.setValue(null, true);
            }
            else {
                this.setValue(result, true);
            }
            this.loading = false;
            this.isStale = false;
            this.statusChanged();
        }
        catch (error) {
            this.loading = false;
            this.error = error;
            this.setError(true);
            this.onError(error);
        }
    }
}
exports.default = AsyncValueHolder;
//# sourceMappingURL=AsyncValueHolder.js.map